/**
 * New console — going back does not reload the screen.
 *
 * Every screen fetches on mount, so Back remounted it and asked the API again: the
 * skeleton, then the wait (some of these calls take the better part of a minute), then
 * the list — scrolled to the top. Back should put you where you were.
 *
 * Two parts, both here so the layout mounts them with one hook:
 *
 *  1. A response cache on the shared API client. Every successful GET is kept for a
 *     while, keyed by its URL. For a few seconds after a Back or Forward navigation, a
 *     request identical to one already made is answered from the cache — the screen
 *     renders what it showed before, at once. Outside that window every request goes to
 *     the network as it always has, so a screen opened normally is fresh. Anything that
 *     writes (PUT, POST, PATCH, DELETE) empties the cache, so Back after a triage or an
 *     edit never shows the old answer.
 *
 *  2. Scroll memory. Each history entry's scroll position is recorded; Back returns to
 *     it (following the page as it grows, so a phone's scroll-loaded list reloads the
 *     pages it had), a normal link starts at the top, and a screen that only rewrites its
 *     own URL — a filter, a page — keeps its place.
 *
 * The cache is idle outside the new console: the interceptors are always registered but
 * pass everything through unless the console's layout is mounted.
 */
import { useEffect, useRef } from 'react';
import { useNavigationType } from 'react-router-dom';

import { protectedApi } from '@api/index';

const TTL = 10 * 60 * 1000;   // how long an answer is kept
const MAX_ENTRIES = 80;
const RESTORE_MS = 5000;      // how long after Back the cache answers, and scroll is restored

const store = new Map();
const positions = new Map();
let active = false;
let restoreUntil = 0;

const keyOf = (config) => `${config.baseURL || ''}|${config.url || ''}|${config.params ? JSON.stringify(config.params) : ''}`;
const isJson = (config) => !config.responseType || config.responseType === 'json';

protectedApi.interceptors.request.use((config) => {
  if (!active) return config;
  const method = String(config.method || 'get').toLowerCase();
  if (method !== 'get') {
    store.clear();
    return config;
  }
  if (!isJson(config) || Date.now() > restoreUntil) return config;
  const hit = store.get(keyOf(config));
  if (!hit || Date.now() - hit.at > TTL) return config;
  return { ...config, adapter: () => Promise.resolve({ ...hit.response, config, request: {} }) };
});

protectedApi.interceptors.response.use((response) => {
  const config = (response && response.config) || {};
  if (active && String(config.method || 'get').toLowerCase() === 'get' && isJson(config)
    && response.status >= 200 && response.status < 300) {
    const key = keyOf(config);
    store.delete(key);
    store.set(key, {
      at: Date.now(),
      response: { data: response.data, status: response.status, statusText: response.statusText, headers: response.headers }
    });
    while (store.size > MAX_ENTRIES) store.delete(store.keys().next().value);
  }
  return response;
});

/**
 * Mounted once, by the console's layout. Marks each navigation — during render, so the
 * screen's own effects, which run before the layout's, already see it — and keeps the
 * scroll position of every history entry.
 */
export const useBackRestore = (location) => {
  const navType = useNavigationType();
  const seen = useRef(null);

  if (seen.current !== location.key) {
    const previous = seen.current;
    seen.current = location.key;
    active = true;
    restoreUntil = navType === 'POP' ? Date.now() + RESTORE_MS : 0;
    /* A screen rewriting its own URL (filters, page) stays where it is scrolled. */
    if (navType === 'REPLACE' && previous != null) positions.set(location.key, window.scrollY);
  }

  useEffect(() => {
    active = true;
    return () => {
      active = false;
      store.clear();
    };
  }, []);

  useEffect(() => {
    if (!('scrollRestoration' in window.history)) return undefined;
    const prev = window.history.scrollRestoration;
    window.history.scrollRestoration = 'manual';
    return () => { window.history.scrollRestoration = prev; };
  }, []);

  useEffect(() => {
    const key = location.key;
    let raf = 0;
    let settling = navType === 'POP' && positions.has(key);

    const stop = () => {
      settling = false;
      window.cancelAnimationFrame(raf);
    };

    if (settling) {
      const target = positions.get(key);
      const started = Date.now();
      const step = () => {
        if (!settling) return;
        const max = document.documentElement.scrollHeight - window.innerHeight;
        if (max >= target || Date.now() - started > RESTORE_MS) {
          window.scrollTo(0, Math.max(0, Math.min(target, max)));
          settling = false;
          return;
        }
        /* Follow the page down as it grows, so a list that loads more on scroll brings
           back the pages it had. */
        window.scrollTo(0, Math.max(0, max));
        raf = window.requestAnimationFrame(step);
      };
      step();
    } else if (navType === 'PUSH') {
      window.scrollTo(0, 0);
    }

    const onScroll = () => { if (!settling) positions.set(key, window.scrollY); };
    /* Someone who starts scrolling during the restore has taken over. */
    const onUser = () => stop();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('wheel', onUser, { passive: true });
    window.addEventListener('touchstart', onUser, { passive: true });
    window.addEventListener('keydown', onUser);
    return () => {
      stop();
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('wheel', onUser);
      window.removeEventListener('touchstart', onUser);
      window.removeEventListener('keydown', onUser);
    };
  }, [location.key, navType]);
};
